import { ref, watch } from "@vue/composition-api";

// Turning the switch off only clears the section if everything is still
// selected, so unchecking a single item turns the switch off without wiping
// the rest.
export default function useSelectAllSwitch({ isAllSelected, selectAll, deselectAll, watchSource }) {
  const allSelected = ref(isAllSelected());

  watch(watchSource, () => {
    allSelected.value = isAllSelected();
  });

  watch(allSelected, (value) => {
    if (value) {
      if (!isAllSelected()) selectAll();

      return;
    }

    if (isAllSelected()) deselectAll();
  });

  return { allSelected };
}

// Loose match, like bootstrap-vue does against a v-model array: the DVR returns
// the channels as strings ("0") while the boxes bind numbers (0). A strict
// comparison leaves the switch disagreeing with the boxes it controls, and that
// disagreement ping-pongs them into an endless re-render.
const includesValue = (list, value) => list.some(item => item === value || String(item) === String(value));

// Toggles `values` inside itemData[key] leaving values owned by other sections
// untouched (DeviceMenuAccess is shared by Camera and Device). `values` may be
// a getter for catalogs that load asynchronously; `purgeValues` are legacy
// values to drop whenever the switch writes the array.
export function useArraySelectAllSwitch(itemData, key, values, purgeValues = []) {
  const getValues = typeof values === "function" ? values : () => values;
  const getRemovable = () => getValues().concat(purgeValues);

  return useSelectAllSwitch({
    // An empty catalog is not "all selected": the channels arrive from the DVR.
    isAllSelected: () => {
      const sectionValues = getValues();

      return sectionValues.length > 0 && sectionValues.every(value => includesValue(itemData[key], value));
    },
    selectAll: () => {
      const removable = getRemovable();

      itemData[key] = itemData[key]
        .filter(value => !includesValue(removable, value))
        .concat(getValues());
    },
    deselectAll: () => {
      const removable = getRemovable();

      itemData[key] = itemData[key].filter(value => !includesValue(removable, value));
    },
    watchSource: () => itemData[key]
  });
}

// Flattens options (with or without `children`) to the section's value list.
export function flattenOptionValues(options) {
  return options.reduce(
    (values, option) => values.concat(option.children ? option.children.map(child => child.value) : option.value),
    []
  );
}
